import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";

import { tagsToTagSets } from "@formant/core/common/tagsToTagSets";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import { ITags } from "@formant/core/model/ITags";
import { IView } from "@formant/core/model/IView";
import { useFormantClientContext } from "@formant/rectangle-sdk";
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@formant/ui-kit";
import { Editor, EditorMode } from "@formant/views-editor";
import { IDashboardView } from "@formant/views-editor/src/types/EditorTypes";

import { useStores } from "../../../context/StoreContext";
import { authStore, onlineDevicesStore } from "../../../stores";
import { newView } from "../../HomeTabs/F3.HomeTabs/F3.AddView";
import { regenerateIds } from "../overviewUtils";
import { OverviewPageConst } from "./overviewPageLayout.const";
import { OverviewSkeletonLoader } from "./OverviewSkeletonLoader";

export const OverviewViewName = `Overview v2`;

interface IOverviewV2 {
    filterTags: ITags;
    onOverviewLoaded?: (view: any) => void;
}

export interface IOverviewV2Ref {
    reset: () => void;
}

export const OverviewV2 = forwardRef<IOverviewV2Ref, IOverviewV2>(
    ({ filterTags, onOverviewLoaded }, ref) => {
        const [isResetModuleDialogVisible, setIsResetModuleDialogVisible] =
            useState(false);

        const { viewStore, deviceDetailsStore } = useStores();
        const [context, setContext] = React.useState<any>(undefined);
        const [filter, setFilter] = React.useState<any>(undefined);
        const [view, setView] = React.useState<any>(undefined);
        const [loading, setLoading] = React.useState<boolean>(true);
        const [allDevices, setAllDevices] = React.useState<IDeviceDetails[]>(
            [],
        );
        const [currentlyEditingNodeGraph, setCurrentlyEditingNodeGraph] =
            React.useState<any>(null);

        const { setDeviceContext, setGlobalFilter, selectedDeviceStatuses } =
            useFormantClientContext();

        useEffect(() => {
            setGlobalFilter({ tags: tagsToTagSets(filterTags) });
        }, [filterTags]);

        useImperativeHandle(ref, () => ({
            reset: async () => {
                setIsResetModuleDialogVisible(true);
            },
        }));

        const handleResetModuleConfirm = async () => {
            setIsResetModuleDialogVisible(false);
            setLoading(true);
            const layout = regenerateIds(OverviewPageConst);
            const overviewView = await viewStore.update(view.id, {
                ...view,
                layout,
            });
            const devices = await deviceDetailsStore.fetchList({
                enabled: true,
            });
            setAllDevices(devices);
            setContext({});
            setCurrentlyEditingNodeGraph(null);
            setView(overviewView);
            setLoading(false);
            onOverviewLoaded?.(overviewView);
            setLoading(false);
        };

        useEffect(() => {
            const fetchData = async () => {
                setLoading(true);
                try {
                    const views = await viewStore.list({
                        async: true,
                        force: true,
                    });
                    let findOverviewView = views.find(
                        v => v.name === OverviewViewName,
                    );
                    if (findOverviewView) {
                        if (
                            !findOverviewView.tags["*"] ||
                            findOverviewView.tags["*"] !== "*"
                        ) {
                            const updatedView = await viewStore.update(
                                findOverviewView.id,
                                {
                                    tags: { "*": "*" },
                                },
                            );
                            findOverviewView = {
                                ...findOverviewView,
                                ...updatedView,
                            };
                        }
                    }
                    if (!findOverviewView) {
                        const layout = regenerateIds(OverviewPageConst);
                        const overviewView = newView(
                            OverviewViewName,
                            "dashboard",
                            [],
                            {
                                "*": "*",
                            },
                            null,
                            layout,
                        );
                        findOverviewView =
                            await viewStore.createSimple(overviewView);
                    }
                    const devices = await deviceDetailsStore.fetchList({
                        enabled: true,
                    });
                    setAllDevices(devices);
                    setContext({});
                    setCurrentlyEditingNodeGraph(null);
                    setView(findOverviewView);
                    setLoading(false);
                    onOverviewLoaded?.(findOverviewView);
                } catch (error) {
                    console.error("Error fetching data:", error);
                    setLoading(false);
                }
            };

            fetchData()
                .then(() => {})
                .catch(console.error);
        }, []);

        useEffect(() => {
            let deviceIds = allDevices.map(device => device.id);
            if (selectedDeviceStatuses.length === 1) {
                const onlineDevices = onlineDevicesStore.online();
                if (onlineDevices) {
                    if (selectedDeviceStatuses[0] === "online") {
                        deviceIds = deviceIds.filter(devId =>
                            onlineDevices.has(devId),
                        );
                    } else {
                        deviceIds = deviceIds.filter(
                            devId => !onlineDevices.has(devId),
                        );
                    }
                }
            }
            setFilter({
                deviceIds,
            });
            setDeviceContext({
                type: "devices",
                deviceIds,
            });
        }, [selectedDeviceStatuses, allDevices]);

        const updateView = (_updatedView: IDashboardView) => {};

        return (
            <div className="h-full">
                {loading && <OverviewSkeletonLoader />}
                {!loading && view && (
                    <Editor
                        device={undefined}
                        context={context}
                        filter={filter}
                        storeId={view.id}
                        view={view as IView<"dashboard">}
                        editorMainClassName="!h-full"
                        editorConfig={{
                            hideHeaderTitle: true,
                            lockedMode:
                                authStore.userRole &&
                                authStore.hasAccessByRole("administrator")
                                    ? undefined
                                    : EditorMode.Read,
                        }}
                        errorMessage={console.error}
                        inspectorConfig={{}}
                        currentlyEditingNodeGraph={currentlyEditingNodeGraph}
                        onChangeView={() => {}}
                        onChangeLayout={() => {}}
                        onChangeUpdatedView={updateView}
                    />
                )}
                <Dialog
                    open={isResetModuleDialogVisible}
                    onOpenChange={setIsResetModuleDialogVisible}
                >
                    <DialogContent
                        onClick={e => {
                            e.stopPropagation();
                        }}
                        className="min-w-[20rem]"
                    >
                        <DialogHeader>
                            <DialogTitle>Reset Overview Module</DialogTitle>
                            <DialogDescription>Are you sure?</DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button
                                variant="tertiary"
                                onClick={() =>
                                    setIsResetModuleDialogVisible(false)
                                }
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleResetModuleConfirm}
                            >
                                Reset
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        );
    },
);
